import { useNavigate } from 'react-router-dom';
import { Home, ArrowRight } from 'lucide-react';
import { useBlogs } from '../hooks/useBlogs';
import { format } from 'date-fns';

const Blogs = () => {
  const navigate = useNavigate();
  const { blogs, loading, error } = useBlogs(true);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Fixed Navigation - only on mobile */}
      <nav className="sm:hidden fixed top-0 left-0 right-0 bg-black border-b border-white/10 z-50">
        <div className="px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <Home className="w-4 h-4" />
              <span className="text-sm">Home</span>
            </button>
            
            <button
              onClick={() => navigate('/projects')}
              className="text-white/60 hover:text-white transition-colors text-sm"
            >
              Projects
            </button>
            
            <button
              onClick={() => navigate('/research')}
              className="text-white/60 hover:text-white transition-colors text-sm"
            >
              Research
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content - padding-top only on mobile for fixed nav */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-8 pb-8 sm:pb-12">
        {/* Navigation - desktop only */}
        <nav className="hidden sm:block mb-12">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>
            
            <button
              onClick={() => navigate('/projects')}
              className="text-white/60 hover:text-white transition-colors"
            >
              Projects
            </button>
            
            <button
              onClick={() => navigate('/research')}
              className="text-white/60 hover:text-white transition-colors"
            >
              Research
            </button>
          </div>
        </nav>
        {/* Header */}
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-light">Writings</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-white/40">Loading posts...</div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-red-400">Error loading posts: {error}</div>
        )}

        {/* Blog Posts List */}
        {!loading && !error && (
          <div className="space-y-6 sm:space-y-8">
            {blogs.length === 0 ? (
              <div className="text-white/40">No blog posts yet. Check back soon!</div>
            ) : (
              blogs.map((blog) => (
                <button
                  key={blog.id}
                  onClick={() => navigate(`/writings/${blog.slug}`)}
                  className="group block w-full text-left border-b border-white/10 pb-6 sm:pb-8 last:border-b-0 cursor-pointer"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-2">
                    <h3 className="text-xl sm:text-2xl font-light text-white group-hover:text-white/80 transition-colors flex-1">
                      {blog.title}
                      {blog.featured && (
                        <span className="ml-2 text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">
                          Featured
                        </span>
                      )}
                    </h3>
                    <ArrowRight className="hidden sm:block w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0" />
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-white/40 text-xs sm:text-sm mb-3">
                    <span>{formatDate(blog.date)}</span>
                    <span>•</span>
                    <span>{blog.readTime}</span>
                  </div>

                  <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-3">
                    {blog.description}
                  </p>

                  {blog.tags && blog.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {blog.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="text-xs px-2 py-0.5 rounded bg-white/10 text-white/50"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;